import { useEffect, useState } from "react";
import { useConvex, useQuery } from "convex/react";
import { setSmartViewTitles } from "@/smart-views/client/title-cache";
import { runSuggestionAgent } from "@/smart-views/agent/runSuggestionAgent";
import { useAppStore } from "@/store";
import {
  SidebarSection,
  SidebarSectionEmpty,
  SidebarSectionHeader,
  SidebarSectionHeaderSlotName,
  SidebarSectionItem,
  SidebarSectionSlotName,
} from "@/components/layout/components/sidebar/sidebar-section";
import { observer } from "mobx-react-lite";
import { SeeAllButton } from "./sidebar-contents.style";
import {
  MDSButtonGroup,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSIconKind,
  MDSText,
  MDSTextVariant,
  MDSTextColor,
} from "@/mds";
import { CreateSmartViewModal } from "@/smart-views/components/CreateSmartViewModal";
import { api } from "@/smart-views/client/api";
import { isConvexConfigured } from "@/smart-views/client/convex-client";
import { hasSmartViewUnreadUpdate } from "@/smart-views/client/update-notifications";
import { SMART_VIEW_ICON_KIND } from "@/smart-views/icons";

interface SidebarContentsSmartViewsProps {
  onClick?: () => void;
}

export const SidebarContentsSmartViews = observer(function SidebarContentsSmartViews({
  onClick,
}: SidebarContentsSmartViewsProps) {
  const { store } = useAppStore();
  const convex = useConvex();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const smartViewsEnabled = isConvexConfigured();
  const smartViews = useQuery(
    api.smartViews.client.listSmartViews,
    smartViewsEnabled ? {} : "skip"
  );
  const hasItems = (smartViews?.length ?? 0) > 0;

  useEffect(() => {
    if (smartViews) setSmartViewTitles(smartViews);
  }, [smartViews]);

  // Kick off a suggestion run on mount (skipped internally if recent).
  useEffect(() => {
    if (!isConvexConfigured()) return;
    runSuggestionAgent({ store, convex });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SidebarSection isExpanded={isExpanded}>
        <SidebarSectionHeader
          slot={SidebarSectionSlotName.Header}
          label="Smart Views"
          defaultIconKind={SMART_VIEW_ICON_KIND}
          isExpanded={isExpanded}
          onPress={() => setIsExpanded(!isExpanded)}
        >
          <MDSButtonGroup slot={SidebarSectionHeaderSlotName.ButtonsRight}>
            <MDSTooltip
              config={{
                label: "Create smart view",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                size={MDSIconButtonSize.XSmall}
                iconKind={MDSIconKind.Plus}
                variant={MDSIconButtonVariant.OnChrome}
                onClick={() => setIsCreateOpen(true)}
                rounded
              />
            </MDSTooltip>
          </MDSButtonGroup>
        </SidebarSectionHeader>
        {hasItems &&
          smartViews?.map(view => (
            <SidebarSectionItem
              key={view._id}
              label={view.title}
              iconKind={SMART_VIEW_ICON_KIND}
              hasUnreadUpdate={hasSmartViewUnreadUpdate(view.smartViewId)}
              onClick={() => {
                onClick?.();
                store.navigation.goToSmartView({ smartViewId: view.smartViewId });
              }}
              indent
            />
          ))}
        {smartViewsEnabled && (
          <SeeAllButton
            onClick={() => {
              onClick?.();
              store.navigation.goToSmartViews();
            }}
          >
            See all
          </SeeAllButton>
        )}
        {!hasItems && (
          <SidebarSectionEmpty>
            <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
              {smartViewsEnabled
                ? "Your Smart Views appear here"
                : "Smart Views are temporarily unavailable"}
            </MDSText>
          </SidebarSectionEmpty>
        )}
      </SidebarSection>
      <CreateSmartViewModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </>
  );
});
